import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'

import {
  selectUserDataUniqId,
  selectUserData,
  selectUserBuilding,
} from '../../reducers/userDataReducer'
import {
  dispatchSetConversations,
  selectConversations,
  selectUnreadConversationIds,
  selectClosedConversationsData,
  selectAssignedConversationsData,
  selectChatContacts,
  isLoadingMessages,
  isLoadingConversations,
  dispatchSetLoadingMessages,
  dispatchSetLoadingConversations,
  selectRecentlyUpdatedConvId,
  selectHighManagementUsers,
} from '../../reducers/chatReducer'
import { selectPropertyManagers } from '../../reducers/Organisation/propertyManagersReducer'
import { selectShiftManagers } from '../../reducers/Organisation/shiftManagersReducer'
import { selectStaffPersons } from '../../reducers/maintenanceStaffReducer'
import {
  selectBuildingsForFilter,
  formatBuildingsForFilter,
  setBuildings,
  setActiveBuilding,
  selectActiveBuilding,
} from '../../reducers/buildingSelectorReducer'
import { selectClients, getClients } from '../../reducers/clientsReducer'
import { readChatRoomFirebase } from '../../firebase/privateChat/firebaseActions'
import API from '../../api/api'
import { getAssetLink } from '../../helpers/get-asset-link'
import ConfirmPopup from '../Popups/ConfirmPopup'

import ChatSidebar from './ChatSidebar/ChatSidebar'
import ChatContactsWindow from './ChatSidebar/ChatContactsWindow'
import ChatBody from './Body/ChatBody'
import ConversationInfoBar from './Body/ConversationInfoBar'
import SettingsSidebar from './SettingsSidebar/SettingsSidebar'
import InboxSettings from './InboxSettings/InboxSettings'
import { TwilioService } from './services/twilio-service'
import { conversationsFilters, conversationStatusMap } from './helpers'

const Chat = ({
  currentUserId,
  currentUserData,
  conversations,
  unreadConversationIds,
  closedConversationsData,
  assignedConversationData,
  clients,
  chatContacts,
  getClients,
  dispatchSetConversations,
  setActiveBuilding,
  propertyManagers,
  shiftManagers,
  staffPeople,
  buildings,
  setBuildings,
  currentBuilding,
  isLoadingMessages,
  isLoadingConversations,
  dispatchSetLoadingMessages,
  dispatchSetLoadingConversations,
  recentlyUpdatedConversationId,
  highManagementUsers,
}) => {
  const { t } = useTranslation(['chat', 'common'])
  const [newChatWindowVisible, setNewChatWindowVisible] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [selectedChat, setSelectedChat] = useState(null)
  const [chatIdToBeSelectedAfterLoad, setChatIdToBeSelectedAfterLoad] = useState(null)
  const [selectedInbox, setSelectedInbox] = useState()
  const [showSettings, setShowSettings] = useState(false)
  const [textingOptOutInfo, setTextingOptOutInfo] = useState(false)
  const [chatNotes, setChatNotes] = useState(null)
  const [search, setSearch] = useState('')
  const [conversationAttributeFilter, setConversationAttributeFilter] = useState('open')

  const navigate = useNavigate()
  const location = useLocation()

  const currentBuildingId = currentBuilding?.id
  const currentRole = currentUserData.user.role

  const queryParams = new URLSearchParams(location.search)
  const queryBuildingId = queryParams.get('buildingId')
  const queryChannelName = queryParams.get('channelName')
  const queryInboxFilter = queryParams.get('queryInboxFilter')
  const settings = queryParams.get('settings')
  const assignedToMe = queryParams.get('assignedToMe')

  const { isLoading, getFilteredByRoleInboxes, checkIfCanSeeConversationByUserAndInboxType } =
    useGetInboxesWithCustom({
      buildingId: currentBuildingId,
      chatCustom: currentBuilding?.chatCustom,
    })

  useEffect(() => {
    if (isLoading || !currentUserData) return
    const filteredInboxes = getFilteredByRoleInboxes(currentUserData)
    // NOTE: inboxes refetch on every consumer mount/focus (staleTime 0), which re-runs this
    // effect - keep the user's selection while it's still available instead of resetting it
    setSelectedInbox((prev) => {
      if (prev === 'ALL' && filteredInboxes.length > 1) return prev
      const stillAvailable = prev?.id && filteredInboxes.find((inbox) => inbox.id === prev.id)
      if (stillAvailable) return stillAvailable
      return filteredInboxes.length > 1 ? 'ALL' : filteredInboxes[0]
    })
  }, [isLoading, getFilteredByRoleInboxes, currentUserData])

  useEffect(() => {
    if (currentRole === 'PropertyManager') {
      setBuildings(currentUserData.buildingIds)
    }
  }, [])

  const handleOpenChat = async () => {
    try {
      await API.reopenChat(selectedChat.channelName, currentBuildingId)
    } catch (error) {
      console.error(error.message)
    }
  }

  const handleCloseChat = async () => {
    try {
      await API.closeChat(selectedChat.channelName, currentBuildingId)
      setIsConfirmModalOpen(false)
    } catch (error) {
      console.error(error.message)
    }
  }

  const onCreateMaintenanceReqChat = (conversationName) => {
    const conversationUserId = conversationName.split('_')[0]
    const requestId = conversationName.split('_')[2]
    const dataAboutChat = {
      teamType: 'MTRQ',
      conversationUserId,
      requestId,
    }

    API.createOrJoinConversation(dataAboutChat)
      .then(async (conversation) => {
        const newConversation = TwilioService.getInstance().parseConversation(conversation)
        const person =
          chatContacts &&
          chatContacts.find(
            (contact) =>
              Number(contact.id) ===
              Number(newConversation.name.slice(0, newConversation.name.indexOf('_'))),
          )
        const conversationToCreate = {
          ...newConversation,
          channelName: conversationName,
          avatar: person ? person.avatar : '',
          name: person ? `${person.firstName} ${person.lastName}` : '',
          role: person ? person.role : '',
          email: person ? person.email : '',
          phone: person ? person.phone : '',
          apartment: person ? person.apartment : '',
          address: person ? `${person.street} #${person.apartment}` : '',
        }
        await dispatchSetConversations(conversations.concat(conversationToCreate))
        setChatIdToBeSelectedAfterLoad(conversationToCreate.channelName)
      })
      .catch((err) => {
        console.error(err.message)
        dispatchSetLoadingMessages(false)
        dispatchSetLoadingConversations(false)
      })
      .finally(() => {
        dispatchSetLoadingConversations(false)
      })
  }

  const handleMaintennaceReqChat = (conversationName) => {
    const searchedChat = conversations.find((conv) => conv.channelName === conversationName)
    dispatchSetLoadingMessages(true)

    if (searchedChat) {
      setSelectedChat(searchedChat)
    } else {
      dispatchSetLoadingConversations(true)
      onCreateMaintenanceReqChat(conversationName)
    }
  }

  const getAvatarById = (id) => {
    if (!id) return getAssetLink({ path: 'chitown-avatar/avatar', file: 'avatar.png' })
    const user = [
      ...chatContacts,
      ...[...propertyManagers, ...staffPeople].map((pm) => pm.user),
      currentUserData.user,
    ]
      .filter((x) => x)
      .find((contact) => String(contact.id) === String(id))
    return getAssetLink({ path: 'chitown-avatar/avatar', file: user ? user.avatar : 'avatar.png' })
  }

  const fetchContacts = async (skip, searchingQuery, isScrolling) => {
    const skipMore =
      clients.length % 15 === 0
        ? clients.length / 15
        : (clients.length - (clients.length % 15)) / 15
    if (typeof searchingQuery === 'undefined') {
      try {
        await getClients(
          typeof skip !== 'undefined' ? skip : skipMore + 1,
          currentBuildingId,
          currentUserId,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery !== '' && isScrolling) {
      try {
        await getClients(
          typeof skip !== 'undefined' ? skip : skipMore + 1,
          currentBuildingId,
          currentUserId,
          searchingQuery,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery !== '' && !isScrolling) {
      try {
        await getClients(
          typeof skip !== 'undefined' ? skip : searchingQuery ? 1 : skipMore + 1,
          currentBuildingId,
          currentUserId,
          searchingQuery,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else if (searchingQuery === '' && isScrolling) {
      try {
        await getClients(
          typeof skip !== 'undefined' ? skip : skipMore + 1,
          currentBuildingId,
          currentUserId,
        )
      } catch (err) {
        throw new Error(err)
      }
    } else {
      try {
        await getClients(1, currentBuildingId, currentUserId)
      } catch (err) {
        throw new Error(err)
      }
    }
  }

  useEffect(() => {
    if (assignedToMe) {
      setConversationAttributeFilter('assignedToYou')
      navigate('/dashboard/chat')
    }
  }, [assignedToMe])

  const filteredConversationsToDisplay = conversations
    .filter((conversation) =>
      checkIfCanSeeConversationByUserAndInboxType(conversation, currentUserData, selectedInbox),
    )
    .map((conversation) => {
      return {
        ...conversation,
        lastMessageAvatar: getAvatarById(conversation.lastMessageSenderIDfromTeam),
      }
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))

  const counts = useMemo(() => {
    let filteredConv = filteredConversationsToDisplay

    if (search) {
      filteredConv = filteredConv.filter((conversation) =>
        conversation.name.toLowerCase().includes(search.toLowerCase()),
      )
    }

    return {
      open: filteredConv.filter((conv) => conv.status === conversationStatusMap.OPEN).length,
      closed: filteredConv.filter((conv) => conv.status === conversationStatusMap.CLOSED).length,
      archived: filteredConv.filter((conv) => conv.status === conversationStatusMap.ARCHIVED)
        .length,
      assignedToYou: filteredConv.filter((conv) =>
        assignedConversationData.some(
          (assigned) =>
            assigned.assignee === currentUserData.user.id && assigned.name === conv.channelName,
        ),
      ).length,
      unassigned: filteredConv.filter(
        (conv) => !assignedConversationData.some((a) => a.name === conv.channelName),
      ).length,
    }
  }, [filteredConversationsToDisplay, assignedConversationData, currentUserData, search])

  const filterOptions = React.useMemo(() => {
    return conversationsFilters.map((filter) => ({
      ...filter,
      label: filter.label,
    }))
  }, [counts])

  useEffect(() => {
    if (recentlyUpdatedConversationId && currentUserId) {
      const searchConversation = conversations.find(
        (conversation) => conversation.id === recentlyUpdatedConversationId,
      )
      if (
        searchConversation &&
        selectedChat &&
        selectedChat.channelName === searchConversation.channelName
      ) {
        readChatRoomFirebase(currentBuildingId, searchConversation.channelName)
      }
    }
  }, [recentlyUpdatedConversationId])

  useEffect(() => {
    if (currentBuildingId) {
      setSelectedChat(null)
    }
  }, [currentBuildingId])

  useEffect(() => {
    if (!isLoadingConversations && queryChannelName) {
      handleMaintennaceReqChat(queryChannelName)
      navigate('/dashboard/chat')
    }
  }, [isLoadingConversations])

  useEffect(() => {
    if (settings) {
      setShowSettings(true)
      navigate('/dashboard/chat')
    }
  }, [settings])

  useEffect(() => {
    if (isLoading || !currentUserData || !queryInboxFilter) return
    const filteredInboxes = getFilteredByRoleInboxes(currentUserData)

    if (queryInboxFilter === 'ALL' || filteredInboxes.length <= 1) {
      setSelectedInbox(queryInboxFilter)
    } else {
      const foundInbox = filteredInboxes.find((inbox) => inbox.id.toString() === queryInboxFilter)
      setSelectedInbox(foundInbox ?? filteredInboxes[0])
    }

    navigate('/dashboard/chat')
  }, [isLoading, getFilteredByRoleInboxes, currentUserData, queryInboxFilter])

  useEffect(() => {
    if (chatIdToBeSelectedAfterLoad) {
      const searchedChat = conversations.find(
        (conversation) => conversation.channelName === chatIdToBeSelectedAfterLoad,
      )
      if (searchedChat?.channelName && searchedChat.channelName !== selectedChat?.channelName) {
        dispatchSetLoadingMessages(true)
        setSelectedChat(searchedChat)
        setChatIdToBeSelectedAfterLoad(null)
      }
    }
  }, [conversations, chatIdToBeSelectedAfterLoad, selectedChat])

  useEffect(() => {
    if (selectedChat) {
      try {
        API.getChatAdditionalInfo(selectedChat.channelName).then(({ data }) => {
          setTextingOptOutInfo(data.smsOptOut)
          setChatNotes(data.notes)
        })
      } catch (err) {
        console.error(err.message)
      }
    }
  }, [selectedChat])

  useEffect(() => {
    if (queryBuildingId && currentBuildingId !== Number(queryBuildingId)) {
      const buildingToSelect = buildings.find((x) => x.id === queryBuildingId)
      setActiveBuilding(buildingToSelect)
    }
  }, [queryBuildingId, buildings.length])

  useEffect(() => {
    if (!selectedChat?.channelName) return
    const foundChat = conversations.find((chat) => chat.channelName === selectedChat.channelName)
    if (foundChat) setSelectedChat(foundChat)
  }, [conversations, selectedChat])

  return (
    <div className="chat">
      <SettingsSidebar
        currentRole={currentRole}
        showSettings={() => setShowSettings(true)}
        showChat={() => setShowSettings(false)}
      />
      {showSettings ? (
        <InboxSettings currentBuilding={currentBuilding} />
      ) : (
        <React.Fragment>
          <ChatSidebar
            conversations={filteredConversationsToDisplay || []}
            createChat={setNewChatWindowVisible}
            selectChat={(chat) => {
              dispatchSetLoadingMessages(true)
              setSelectedChat(chat)
            }}
            selectedChat={selectedChat}
            selectedChannelFilter={selectedInbox}
            unreadConversationIds={unreadConversationIds}
            closedConversationsData={closedConversationsData}
            assignedConversationData={assignedConversationData}
            isLoadingConversations={isLoadingConversations}
            currentUserData={currentUserData}
            selectChannel={setSelectedInbox}
            search={search}
            setSearch={setSearch}
            conversationAttributeFilter={conversationAttributeFilter}
          />
          <ChatBody
            selectedChat={selectedChat}
            isLoading={isLoadingMessages}
            currentBuildingId={currentBuildingId}
            currentUserData={currentUserData}
            managementUsers={[...propertyManagers, ...shiftManagers, ...staffPeople]}
            highManagementUsers={highManagementUsers}
            assignedConversationData={assignedConversationData}
            currentBuildingTimezone={currentBuilding?.timezone ?? 'America/Chicago'}
            closedConversationsData={closedConversationsData}
            textingOptOutInfo={textingOptOutInfo}
            cancelLoading={() => dispatchSetLoadingMessages(false)}
            isAiWriterAvailable={currentBuilding?.aiWriterAvailable}
            conversationAttributeFilter={conversationAttributeFilter}
            setConversationAttributeFilter={setConversationAttributeFilter}
            counts={counts}
            filterOptions={filterOptions}
          />
          <ConversationInfoBar
            selectedChat={selectedChat}
            textingOptOutInfo={textingOptOutInfo}
            chatNotes={chatNotes}
            setChatNotes={setChatNotes}
            isLoading={isLoadingConversations}
            selectChatByConversationName={handleMaintennaceReqChat}
            handleOpenChat={handleOpenChat}
            handleCloseChat={() => setIsConfirmModalOpen(true)}
            managementUsers={[...propertyManagers, ...shiftManagers, ...staffPeople]}
            currentRole={currentRole}
          />
        </React.Fragment>
      )}
      {newChatWindowVisible && (
        <ChatContactsWindow
          selectedInbox={selectedInbox}
          chatContacts={chatContacts}
          handleCancel={() => setNewChatWindowVisible(false)}
          fetchContacts={(query, isScrolling) => fetchContacts(undefined, query, isScrolling)}
          openChat={(chatId) => setChatIdToBeSelectedAfterLoad(chatId)}
        />
      )}
      <ConfirmPopup
        isOpen={isConfirmModalOpen}
        className="is-success"
        title={t('chat:closeChatPopup.closeChat')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('chat:closeChatPopup.closeChatButton')}
        confirmCallback={handleCloseChat}
        cancelCallback={() => setIsConfirmModalOpen(false)}
        body={
          <div className="close-confirm-texts">
            <p className="main-text">{t('chat:closeChatPopup.mainText')}</p>
            <p className="sub-note">{t('chat:closeChatPopup.subNote')}</p>
          </div>
        }
      />
    </div>
  )
}

Chat.propTypes = {
  currentUserId: PropTypes.number,
  currentUserData: PropTypes.object,
  conversations: PropTypes.array,
  unreadConversationIds: PropTypes.arrayOf(PropTypes.string),
  closedConversationsData: PropTypes.arrayOf(PropTypes.object),
  assignedConversationData: PropTypes.arrayOf(PropTypes.object),
  clients: PropTypes.array,
  chatContacts: PropTypes.array,
  dispatchSetConversations: PropTypes.func,
  setActiveBuilding: PropTypes.func,
  getClients: PropTypes.func,
  shiftManagers: PropTypes.array,
  propertyManagers: PropTypes.array,
  staffPeople: PropTypes.array,
  buildings: PropTypes.array,
  setBuildings: PropTypes.func,
  currentBuildingId: PropTypes.number,
  isLoadingMessages: PropTypes.bool,
  isLoadingConversations: PropTypes.bool,
  dispatchSetLoadingMessages: PropTypes.func,
  dispatchSetLoadingConversations: PropTypes.func,
  recentlyUpdatedConversationId: PropTypes.string,
  highManagementUsers: PropTypes.array,
}

export default connect(
  (state) => {
    const currentUserData = selectUserData(state)
    return {
      currentUserData,
      currentBuilding: selectActiveBuilding(state),
      currentUserId: selectUserDataUniqId(state),
      conversations: selectConversations(state),
      unreadConversationIds: selectUnreadConversationIds(state),
      closedConversationsData: selectClosedConversationsData(state),
      assignedConversationData: selectAssignedConversationsData(state),
      clients: selectClients(state),
      chatContacts: selectChatContacts(state),
      shiftManagers: selectShiftManagers(state),
      propertyManagers: selectPropertyManagers(state),
      staffPeople: selectStaffPersons(state),
      buildings: ['ShiftManager', 'Staff'].includes(currentUserData.user.role)
        ? formatBuildingsForFilter([selectUserBuilding(state)])
        : selectBuildingsForFilter(state),
      isLoadingMessages: isLoadingMessages(state),
      isLoadingConversations: isLoadingConversations(state),
      recentlyUpdatedConversationId: selectRecentlyUpdatedConvId(state),
      highManagementUsers: selectHighManagementUsers(state),
    }
  },
  {
    setActiveBuilding,
    dispatchSetConversations,
    getClients,
    setBuildings,
    dispatchSetLoadingMessages,
    dispatchSetLoadingConversations,
  },
)(Chat)
